import { Goal } from "@/models/Goal.model";
import { AbstractGoalLoader, PromiseCache } from "@/goals/utils/AbstractGoalLoader.util";
import type { AxiosInstance } from "axios";
import type { GoalSourceContent, GoalSourceContentItem } from "@/utils/GoalSource";
import {
	OPSTAP_KRC_API_URL,
	OPSTAP_DOCUMENT_KEY,
	OPSTAP_SNAPSHOT,
	OPSTAP_CURRICULUM_SOURCE_NAME,
} from "@/goals/opstap/settings";

interface KrcItem {
	href: string;
	key: string;
	type: string;
	title?: string;
	description?: string;
	identifier?: string;
	parentHref?: string;
}

// Module-level cache of the parsed krcItems document, keyed by the document hash so a
// changed hash triggers a fresh fetch. Persists across loader instances.
const documentCache = new PromiseCache<Map<string, KrcItem>>();

/** Test helper: clear the module-level cache between tests. */
export function resetOpStapCache(): void {
	documentCache.clear();
}

export class OpStapLoader extends AbstractGoalLoader {

	private apiClient: AxiosInstance;

	public constructor() {
		super();
		this.apiClient = this.createRetryingClient(OPSTAP_KRC_API_URL);
	}

	private get basePath(): string {
		return `/documents/${OPSTAP_DOCUMENT_KEY}/snapshots/${OPSTAP_SNAPSHOT}/krcItems`;
	}

	async load(selectedKeys: string[]): Promise<Goal[]> {
		const map = await this.getItemMap();

		const goals: Goal[] = [];
		for (const href of selectedKeys) {
			const item = map.get(href);
			if (!item || item.type !== 'KRC_CURRICULUM_GOAL') {
				continue;
			}
			goals.push(this.transformToGoal(item, map));
		}
		return goals;
	}

	/**
	 * Build a discipline > domain display skeleton (GoalSourceContent) from the KRC
	 * document. `path`s are the dotted identifiers goals' fullPath resolves against
	 * ("1", "1.1"); domains are left as empty containers (structure_only).
	 * Rejects if the KRC document cannot be fetched.
	 */
	async loadStructure(): Promise<GoalSourceContent> {
		const map = await this.getItemMap();

		// One skeleton node per discipline, keyed by its href.
		const disciplineNodes = new Map<string, GoalSourceContentItem>();
		for (const item of map.values()) {
			if (item.type === 'KRC_CURRICULUM_DISCIPLINE' && item.identifier) {
				disciplineNodes.set(item.href, {
					index: 0,
					path: item.identifier,
					title: item.title ?? '',
					info: '',
					items: [],
				});
			}
		}

		// Attach each domain to its discipline ancestor.
		for (const item of map.values()) {
			if (item.type !== 'KRC_CURRICULUM_DOMAIN' || !item.identifier) {
				continue;
			}
			const discipline = this.findAncestorOfType(item, map, 'KRC_CURRICULUM_DISCIPLINE');
			if (!discipline || !discipline.identifier) {
				continue;
			}
			const disciplineNode = disciplineNodes.get(discipline.href);
			if (!disciplineNode) {
				continue;
			}
			disciplineNode.items.push({
				index: 0,
				path: discipline.identifier + '.' + item.identifier,
				title: item.title ?? '',
				info: '',
				items: [],
			});
		}

		return {
			copyright: '',
			name: '',
			version: '',
			structure_only: true,
			items: Array.from(disciplineNodes.values()),
		};
	}

	/** Walk up parentHref until an item of the given type is found. */
	private findAncestorOfType(item: KrcItem, map: Map<string, KrcItem>, type: string): KrcItem | undefined {
		const visited = new Set<string>();
		let current = item.parentHref ? map.get(item.parentHref) : undefined;
		while (current) {
			if (current.type === type) {
				return current;
			}
			if (current.href && visited.has(current.href)) {
				break;
			}
			if (current.href) {
				visited.add(current.href);
			}
			current = current.parentHref ? map.get(current.parentHref) : undefined;
		}
		return undefined;
	}

	private async getItemMap(): Promise<Map<string, KrcItem>> {
		const hashResponse = await this.apiClient.get(`${this.basePath}/hash`);
		const hash = hashResponse.data.hash;
		if (!documentCache.has(hash)) {
			// Only the current document is relevant; release any superseded (different-hash) entry.
			documentCache.clear();
		}
		return documentCache.getOrCreate(hash, () => this.fetchAndBuildMap());
	}

	private async fetchAndBuildMap(): Promise<Map<string, KrcItem>> {
		const response = await this.apiClient.get(this.basePath);
		const map = new Map<string, KrcItem>();
		for (const item of response.data.items) {
			map.set(item.href, item);
		}
		return map;
	}

	private transformToGoal(item: KrcItem, map: Map<string, KrcItem>): Goal {
		const goal = new Goal();
		goal.source = OPSTAP_CURRICULUM_SOURCE_NAME;
		goal.sourceId = this.extractIdFromUrl(item.href);
		goal.title = this.sanitizeContent(item.title ?? '');
		goal.info = item.description ? this.sanitizeContent(item.description) : null;
		goal.sourceParentPath = this.buildParentPath(item, map);
		// The KRC goal identifier already includes the discipline.domain prefix (e.g. "2.1.GJK.1"),
		// which is exactly the parent path ("2.1"). Strip that overlap so fullPath
		// (parentPath + "." + pathId) doesn't repeat it (e.g. "2.1.2.1.GJK.1").
		let pathId = item.identifier ?? null;
		if (pathId && goal.sourceParentPath && pathId.startsWith(goal.sourceParentPath + '.')) {
			pathId = pathId.slice(goal.sourceParentPath.length + 1);
		}
		goal.sourcePathId = pathId;
		goal.tags = this.collectTags(item, map);
		goal.color = null;
		return goal;
	}

	/** Dotted path of the DISCIPLINE and DOMAIN ancestor identifiers, top-down (e.g. "1.1"). */
	private buildParentPath(item: KrcItem, map: Map<string, KrcItem>): string {
		const segments: string[] = [];
		const visited = new Set<string>();
		let current = item.parentHref ? map.get(item.parentHref) : undefined;
		while (current) {
			if (current.href && visited.has(current.href)) {
				break;
			}
			if (current.href) {
				visited.add(current.href);
			}
			if (
				(current.type === 'KRC_CURRICULUM_DISCIPLINE' || current.type === 'KRC_CURRICULUM_DOMAIN')
				&& current.identifier
			) {
				segments.unshift(current.identifier);
			}
			current = current.parentHref ? map.get(current.parentHref) : undefined;
		}
		return segments.join('.');
	}

	/**
	 * Titles of the goal's ancestor nodes between the domain and the goal — subdomain, cluster,
	 * goal-set, age-range, etc. — surfaced as tags. The curriculum root, disciplines and domains
	 * are excluded (disciplines/domains form the dotted path, not tags). Top-down (root-most
	 * first), de-duplicated, empties dropped.
	 */
	private collectTags(item: KrcItem, map: Map<string, KrcItem>): string[] {
		const NON_TAG_TYPES = new Set([
			'KRC_CURRICULUM',
			'KRC_CURRICULUM_DISCIPLINE',
			'KRC_CURRICULUM_DOMAIN',
			'KRC_GOAL_SET_ITEM',
		]);

		const titles: string[] = [];
		const visited = new Set<string>();
		let current = item.parentHref ? map.get(item.parentHref) : undefined;
		while (current) {
			if (current.href && visited.has(current.href)) {
				break;
			}
			if (current.href) {
				visited.add(current.href);
			}
			if (!NON_TAG_TYPES.has(current.type) && current.title) {
				const tag = this.sanitizeContent(current.title);
				if (tag) {
					titles.unshift(tag); // build root-most first (top-down)
				}
			}
			current = current.parentHref ? map.get(current.parentHref) : undefined;
		}

		// De-duplicate, keeping the first (root-most) occurrence.
		return titles.filter((tag, index) => titles.indexOf(tag) === index);
	}

}
